import React from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { BASE_URL } from 'constants/env';

import './stoneFiles.less';

/**
 * Public page for one stone's files. Everything it needs is in the url, so it
 * keeps working after the stone has been removed from the system - which is the
 * whole reason it exists. It never calls the diamond api.
 */

// the source photos are only around 570px wide, so past 2x there is nothing
// left to see - it just gets blocky
const ZOOM_MIN = 1;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;

const SECTIONS = [
  { key: 'image', title: 'Image', type: 'img' },
  { key: 'video', title: 'Video', type: 'videoFile' },
  { key: 'certificate', title: 'Certificate', type: 'certFile' },
];

const parseQuery = (search) => {
  const query = {};
  String(search || '')
    .replace(/^\?/, '')
    .split('&')
    .forEach((pair) => {
      if (!pair) return;
      const [key, value] = pair.split('=');
      query[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });
  return query;
};

const fileUrl = (type, id, inline) =>
  `${BASE_URL}/web/v1/diamond/file?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}${
    inline ? '&inline=1' : ''
  }`;

// every file this stone has, zipped by the api
const bundleUrl = (packetNo, reportNo) =>
  `${BASE_URL}/web/v1/diamond/files?type=all&ids=${encodeURIComponent(packetNo)}${
    reportNo ? `&rpt=${encodeURIComponent(reportNo)}` : ''
  }`;

const StoneFiles = () => {
  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const packetNo = match.params.id;
  const query = React.useMemo(() => parseQuery(location.search), [location.search]);
  const reportNo = query.rpt || '';

  const idFor = React.useCallback((key) => (key === 'certificate' ? reportNo : packetNo), [packetNo, reportNo]);

  const requested = SECTIONS.some((section) => section.key === query.tab) ? query.tab : 'image';
  const [active, setActive] = React.useState(idFor(requested) ? requested : 'image');
  const [failed, setFailed] = React.useState({});
  const [zoomed, setZoomed] = React.useState(false);
  const [zoom, setZoom] = React.useState(ZOOM_MIN);

  const section = SECTIONS.find((item) => item.key === active) || SECTIONS[0];
  const activeId = idFor(active);
  const isBroken = Boolean(failed[active]);

  const markFailed = React.useCallback((key) => setFailed((prev) => ({ ...prev, [key]: true })), []);

  const selectSection = React.useCallback(
    (key) => {
      setActive(key);
      history.replace({ ...location, search: `?tab=${key}${reportNo ? `&rpt=${encodeURIComponent(reportNo)}` : ''}` });
    },
    [history, location, reportNo],
  );

  const stepZoom = React.useCallback((by) => {
    setZoom((current) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((current + by) * 100) / 100)));
  }, []);

  React.useEffect(() => {
    if (!zoomed) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setZoomed(false);
      if (event.key === '+' || event.key === '=') stepZoom(ZOOM_STEP);
      if (event.key === '-' || event.key === '_') stepZoom(-ZOOM_STEP);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed, stepZoom]);

  const renderStage = () => {
    if (!activeId) {
      return (
        <p className="stoneStageNote">
          This stone has no {section.title.toLowerCase()} on record.
        </p>
      );
    }
    if (isBroken) {
      return (
        <p className="stoneStageNote">
          The {section.title.toLowerCase()} did not load. Try the download instead.
        </p>
      );
    }
    if (active === 'image') {
      return (
        <img
          className="stoneStageImage"
          src={fileUrl(section.type, activeId, true)}
          alt={`Stone ${packetNo}`}
          onClick={() => {
            setZoom(ZOOM_MIN);
            setZoomed(true);
          }}
          onError={() => markFailed(active)}
        />
      );
    }
    if (active === 'video') {
      return (
        <video
          className="stoneStageVideo"
          src={fileUrl(section.type, activeId, true)}
          controls
          playsInline
          onError={() => markFailed(active)}
        />
      );
    }
    // a certificate is a single page, so drop the viewer's thumbnail rail and
    // let the page fill the width
    return (
      <iframe
        className="stoneStageCerti"
        src={`${fileUrl(section.type, activeId, true)}#view=FitH&navpanes=0`}
        title="Certificate"
      />
    );
  };

  return (
    <div className="stonePage">
      <header className="stoneHeader">
        <img className="stoneHeaderLogo" src={require('assets/img/sidebar_logo_w.png')} alt="Unique Diamax" />
        <div className="stoneHeaderId">
          <span className="stoneHeaderPacket">{packetNo}</span>
          {reportNo && <span className="stoneHeaderReport">Report {reportNo}</span>}
        </div>
      </header>

      <div className="stoneBar">
        <nav className="stoneTabs">
          {SECTIONS.map((item) => {
            const available = Boolean(idFor(item.key));
            return (
              <button
                key={item.key}
                type="button"
                className={`stoneTab ${active === item.key ? 'is-active' : ''} ${available ? '' : 'is-empty'}`}
                onClick={() => selectSection(item.key)}
              >
                {item.title}
                {!available && <span className="stoneTabNote">none</span>}
              </button>
            );
          })}
        </nav>

        <div className="stoneActions">
          <a className="stoneSave is-quiet" href={bundleUrl(packetNo, reportNo)}>
            Download all
          </a>
          {activeId && (
            <a className="stoneSave" href={fileUrl(section.type, activeId, false)}>
              Download {section.title.toLowerCase()}
            </a>
          )}
        </div>
      </div>

      <main className="stoneStage">{renderStage()}</main>

      {zoomed && (
        <div
          className="stoneZoom"
          onClick={() => setZoomed(false)}
          onWheel={(event) => stepZoom(event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)}
        >
          <div className="stoneZoomControls" onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => stepZoom(-ZOOM_STEP)} disabled={zoom <= ZOOM_MIN}>
              &minus;
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => stepZoom(ZOOM_STEP)} disabled={zoom >= ZOOM_MAX}>
              +
            </button>
            <button type="button" className="stoneZoomClose" onClick={() => setZoomed(false)}>
              Close
            </button>
          </div>
          <img
            src={fileUrl('img', packetNo, true)}
            alt={`Stone ${packetNo}`}
            // the box grows with the zoom and object-fit scales the picture into
            // it, so 1x is exactly "fits the screen" and 2x is twice that
            style={{ width: `${95 * zoom}vw`, height: `${95 * zoom}vh` }}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default StoneFiles;
